// CommandManager - Undo/Redo管理
// アクションの履歴を管理し、元に戻す・やり直し機能を提供

import { MapModel } from "common/model";
import { Vector3D } from "common/types";
import { ActionCommandType } from "features/builder/types";
import { MAX_UNDO_STACK_SIZE } from "features/builder/constants";

// アクションペイロード
export interface ActionPayload {
  model?: MapModel;
  meshId?: string;
  id?: string;
  changes?: { [key: string]: string | Vector3D };
}

// アクション履歴レコード
export interface ActionRecord {
  type: ActionCommandType;
  forward: ActionPayload;
  backward: ActionPayload;
  timestamp: number;
}

export class CommandManager {
  private undoStack: ActionRecord[] = [];
  private redoStack: ActionRecord[] = [];
  private maxStackSize: number;

  constructor(maxStackSize: number = MAX_UNDO_STACK_SIZE) {
    this.maxStackSize = maxStackSize;
  }

  // アクションを記録
  recordAction(
    type: ActionCommandType,
    forward: ActionPayload,
    backward: ActionPayload,
  ): ActionRecord {
    const record: ActionRecord = {
      type,
      forward,
      backward,
      timestamp: Date.now(),
    };

    this.undoStack.push(record);

    // スタックサイズ制限
    if (this.undoStack.length > this.maxStackSize) {
      this.undoStack.shift();
    }

    // 新しいアクションが記録されたらredoスタックをクリア
    this.redoStack = [];

    return record;
  }

  // ADD用の記録作成
  recordAdd(model: MapModel): ActionRecord {
    return this.recordAction(ActionCommandType.ADD_MODE, { model }, { meshId: model.id });
  }

  // REMOVE用の記録作成
  recordRemove(model: MapModel): ActionRecord {
    return this.recordAction(ActionCommandType.REMOVE_MODE, { meshId: model.id }, { model });
  }

  // CHANGE用の記録作成
  recordChange(
    id: string,
    newChanges: { [key: string]: string | Vector3D },
    oldChanges: { [key: string]: string | Vector3D },
  ): ActionRecord {
    return this.recordAction(
      ActionCommandType.CHANGE_MODE,
      { id, changes: newChanges },
      { id, changes: oldChanges },
    );
  }

  // Undoを実行
  undo(): ActionRecord | null {
    const record = this.undoStack.pop();
    if (!record) return null;

    this.redoStack.push(record);
    return record;
  }

  // Redoを実行
  redo(): ActionRecord | null {
    const record = this.redoStack.pop();
    if (!record) return null;

    this.undoStack.push(record);
    return record;
  }

  // Undoが可能か
  canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  // Redoが可能か
  canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  // Undoスタックを取得
  getUndoStack(): ActionRecord[] {
    return [...this.undoStack];
  }

  // Redoスタックを取得
  getRedoStack(): ActionRecord[] {
    return [...this.redoStack];
  }

  // スタックからリストア
  restoreFromStacks(undoStack: ActionRecord[], redoStack: ActionRecord[]): void {
    this.undoStack = [...undoStack];
    this.redoStack = [...redoStack];
  }

  // 履歴をクリア
  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
  }
}
